"use client";

import { forwardRef, useRef, useState } from "react";
import { mergeRefs } from "react-merge-refs";

import {
  PasswordInputButton,
  PasswordInputControl,
  PasswordInputRoot,
  type PasswordInputButtonProps,
  type PasswordInputControlProps,
  type PasswordInputControlRef,
  type PasswordInputRootProps,
  type PasswordInputRootRef,
} from "./password-input.components";
import { formatAriaInvalid } from "./style.utils";
import { useClickAway } from "./useClickAway";

export type PasswordInputRef = PasswordInputControlRef;

export interface PasswordInputProps extends PasswordInputControlProps {
  buttonProps?: PasswordInputButtonProps;
  rootProps?: Omit<PasswordInputRootProps, "className">;
}

const PasswordInput = forwardRef<PasswordInputRef, PasswordInputProps>(
  (
    {
      "aria-invalid": ariaInvalid,
      autoComplete = "current-password",
      className,
      name,
      size,
      disabled,
      buttonProps,
      rootProps,
      ...inputProps
    },
    ref,
  ) => {
    const [isRevealed, setIsRevealed] = useState(false);
    const rootRef = useClickAway<PasswordInputRootRef>(() => {
      if (isRevealed) {
        setIsRevealed(false);
      }
    });
    const inputRef = useRef<HTMLInputElement>(null);

    return (
      <PasswordInputRoot
        {...rootProps}
        className={className}
        invalid={formatAriaInvalid(ariaInvalid)}
        disabled={disabled}
        ref={rootRef}
      >
        <PasswordInputControl
          {...inputProps}
          type={isRevealed ? "text" : "password"}
          name={name}
          size={size}
          disabled={disabled}
          autoComplete={autoComplete}
          spellCheck={false}
          aria-invalid={ariaInvalid}
          ref={mergeRefs([inputRef, ref])}
        />
        <PasswordInputButton
          {...buttonProps}
          onClick={(event) => {
            setIsRevealed((prevState: boolean) => !prevState);
            buttonProps?.onClick?.(event);
          }}
          size={size}
          active={isRevealed}
          invalid={formatAriaInvalid(ariaInvalid)}
          disabled={disabled}
        />
      </PasswordInputRoot>
    );
  },
);

PasswordInput.displayName = "PasswordInput";

export { PasswordInput };
